import { useState, useEffect } from 'react';
import { Page, Tabs, Banner, Button, TextField, InlineStack, Card, BlockStack } from '@shopify/polaris';
import OrderStatusWidget from './components/OrderStatusWidget';
import AdminSettings from './components/AdminSettings';
import FAQManagement from './components/FAQManagement';
import AISettings from './components/AISettings';
import ContactFormSettings from './components/ContactFormSettings';
import AIChatWidget from './components/AIChatWidget';
import SessionCheck from './components/SessionCheck';
import { fetchJsonWithAuth } from './utils/fetchWithAuth';
import { getShopDomain } from './utils/appBridge';

function App() {
  const urlParams = new URLSearchParams(window.location.search);
  const shopParam = urlParams.get('shop') || '';
  const embedded = urlParams.get('embedded') || '';
  const host = urlParams.get('host') || '';
  const view = urlParams.get('view') || '';
  
  // Derive shop from URL param or host parameter (for embedded apps)
  // This ensures we can identify the shop even when Shopify doesn't include shop= in the URL
  const shop = shopParam || getShopDomain() || '';
  
  // PRIORITY 1: Check for explicit view=customer parameter (for iframe embedding)
  if (view === 'customer') {
    return <CustomerApp shop={shop} />;
  }
  
  // PRIORITY 2: Check if it's an admin context:
  // 1. Explicitly /admin path
  // 2. Has 'embedded' or 'host' parameter (Shopify admin embed)
  // 3. Path is exactly '/' with shop param (default admin entry)
  const isAdmin = window.location.pathname.includes('/admin') || 
                  embedded === '1' ||
                  host !== '' ||
                  (window.location.pathname === '/' && shop !== '');

  // If we're in admin context and have a shop (from URL or host), show AdminApp
  if (isAdmin && shop) {
    return <AdminApp shop={shop} />;
  }

  // PRIORITY 3: Customer-facing view if no shop can be determined
  if (!shop) {
    return <CustomerApp shop={shop} />;
  }

  // Default to admin if we have a shop
  return <AdminApp shop={shop} />;
}

function CustomerApp({ shop }: { shop: string }) {
  const [shopStatus, setShopStatus] = useState<'checking' | 'active' | 'uninstalled'>('checking');

  // On mount: check if shop has app installed (settings exist). If 404 = uninstalled.
  useEffect(() => {
    if (!shop) {
      setShopStatus('uninstalled');
      return;
    }

    const apiUrl = window.location.origin;
    const url = `${apiUrl}/api/admin/settings/public?shop=${encodeURIComponent(shop)}`;

    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.timeout = 8000;
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.onload = () => {
      if (xhr.status === 404) {
        setShopStatus('uninstalled');
      } else {
        setShopStatus('active');
      }
    };

    xhr.onerror = () => setShopStatus('uninstalled');
    xhr.ontimeout = () => setShopStatus('uninstalled');
    xhr.send();
  }, [shop]);

  // Uninstalled: show clean disabled message (orphaned page after app uninstall)
  if (shopStatus === 'uninstalled') {
    return (
      <div style={{
        backgroundColor: '#fafbfb',
        minHeight: '400px',
        width: '100%',
        padding: '48px 24px',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '400px' }}>
          <p style={{ fontSize: '32px', marginBottom: '16px' }}>📭</p>
          <p style={{ fontSize: '18px', fontWeight: 600, color: '#202223', marginBottom: '8px' }}>
            This page is no longer active
          </p>
          <p style={{ fontSize: '14px', color: '#6d7175', lineHeight: 1.5 }}>
            The app has been uninstalled from this store. Order tracking, FAQs, and chat support are no longer available here.
          </p>
        </div>
      </div>
    );
  }

  // Still checking
  if (shopStatus === 'checking') {
    return (
      <div style={{
        backgroundColor: 'white',
        minHeight: '300px',
        width: '100%',
        padding: '48px 24px',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <p style={{ fontSize: '14px', color: '#6d7175' }}>Loading...</p>
      </div>
    );
  }

  // Active: show full widgets
  return (
    <div style={{ 
      backgroundColor: 'white', 
      minHeight: '100vh',
      width: '100%',
      padding: '0',
      margin: '0'
    }}>
      <OrderStatusWidget shop={shop} />
      <AIChatWidget shop={shop} />
    </div>
  );
}

function AdminApp({ shop }: { shop: string }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [sessionValid, setSessionValid] = useState(false);
  const [pageName, setPageName] = useState('');
  const [pageNameLoading, setPageNameLoading] = useState(true);
  const [pageNameSaving, setPageNameSaving] = useState(false);
  const [pageNameError, setPageNameError] = useState('');
  const [pageNameSuccess, setPageNameSuccess] = useState(false);

  // Fetch page name when component mounts or session becomes valid
  // MUST be before any conditional returns (React Rules of Hooks)
  useEffect(() => {
    if (sessionValid && shop) {
      const loadPageName = async () => {
        setPageNameLoading(true);
        setPageNameError('');
        try {
          const data = await fetchJsonWithAuth(`/api/admin/page-name?shop=${encodeURIComponent(shop)}`);
          if (data.success && data.pageName) {
            setPageName(data.pageName);
            setPageNameError(''); // Clear any previous errors
          } else {
            // Page doesn't exist yet - this is okay, just show a helpful message
            setPageName('');
            setPageNameError('Page not created yet. Click "Refresh Page" to create it.');
          }
        } catch (error: any) {
          console.error('Error loading page name:', error);
          console.error('Error details:', error);
          // Try to extract error message from response
          let errorMessage = error.message || 'Failed to load page name';
          let requiresReauth = false;
          
          if (error.status === 401 || errorMessage.includes('401') || errorMessage.includes('Authentication')) {
            requiresReauth = true;
            errorMessage = 'Your app session has expired. Please refresh your browser or reinstall the app.';
          } else if (error.response) {
            try {
              const errorData = await error.response.json();
              errorMessage = errorData.message || errorData.error || errorMessage;
              requiresReauth = errorData.requiresReauth || false;
              console.error('Error response data:', errorData);
            } catch (e) {
              // Ignore JSON parse errors
            }
          }
          
          // Check if it's a 404 (page not found) vs other errors
          // Note: 401 errors are handled centrally by fetchWithAuth (App Bridge Redirect)
          // We just show a simple error message here
          if (errorMessage.includes('404') || errorMessage.includes('not found')) {
            setPageName('');
            setPageNameError('Page not created yet. Click "Refresh Page" to create it.');
          } else if (requiresReauth) {
            // Session expired - fetchWithAuth will handle redirect via App Bridge
            // Just show a simple message, no popup or manual redirect
            setPageNameError('Session expired. Redirecting to re-authenticate...');
          } else {
            setPageNameError(errorMessage + '. You can still edit it after refreshing the page.');
          }
        } finally {
          setPageNameLoading(false);
        }
      };
      loadPageName();
    }
  }, [sessionValid, shop]);

  // Show session check/loading screen while validating
  if (!sessionValid) {
    return <SessionCheck shop={shop} onSessionValid={() => setSessionValid(true)} />;
  }

  const handleSavePageName = async () => {
    if (!pageName.trim()) {
      setPageNameError('Page name cannot be empty');
      return;
    }

    setPageNameSaving(true);
    setPageNameError('');
    setPageNameSuccess(false);

    try {
      const data = await fetchJsonWithAuth(`/api/admin/page-name?shop=${encodeURIComponent(shop)}`, {
        method: 'PUT',
        body: JSON.stringify({ pageName: pageName.trim() }),
      });

      if (data.success) {
        setPageName(data.pageName);
        setPageNameSuccess(true);
        setTimeout(() => setPageNameSuccess(false), 3000);
      } else {
        setPageNameError(data.error || 'Failed to update page name');
      }
    } catch (error: any) {
      console.error('Error saving page name:', error);
      setPageNameError(error.message || 'Failed to update page name');
    } finally {
      setPageNameSaving(false);
    }
  };

  const tabs = [
    {
      id: 'faq',
      content: 'FAQ Section',
      panelID: 'faq-panel',
    },
    {
      id: 'settings',
      content: 'Order Status Section',
      panelID: 'settings-panel',
    },
    {
      id: 'ai',
      content: 'AI Assistant Section',
      panelID: 'ai-panel',
    },
    {
      id: 'contact',
      content: 'Contact Form',
      panelID: 'contact-panel',
    },
  ];

  // Construct the preview URL
  const shopDomain = shop.replace('.myshopify.com', '');
  const previewUrl = `https://${shopDomain}.myshopify.com/pages/ultimate-contact-page`;

  const handleRefreshPage = async () => {
    setRefreshing(true);
    setRefreshSuccess(false);
    setRefreshError('');

    try {
      const result = await fetchJsonWithAuth(`/api/admin/refresh-page?shop=${encodeURIComponent(shop)}`, {
        method: 'POST',
      });

      if (result.success) {
        setRefreshSuccess(true);
        setTimeout(() => setRefreshSuccess(false), 5000);
        // Reload page name after successful refresh
        if (sessionValid && shop) {
          setTimeout(() => {
            const loadPageName = async () => {
              setPageNameLoading(true);
              setPageNameError('');
              try {
                const data = await fetchJsonWithAuth(`/api/admin/page-name?shop=${encodeURIComponent(shop)}`);
                if (data.success && data.pageName) {
                  setPageName(data.pageName);
                  setPageNameError(''); // Clear any previous errors
                } else {
                  setPageName('');
                  setPageNameError('Page not created yet. Please try refreshing again.');
                }
              } catch (error: any) {
                console.error('Error loading page name after refresh:', error);
                // Note: 401 errors are handled centrally by fetchWithAuth (App Bridge Redirect)
                if (error.status === 401 || error.message?.includes('401') || error.message?.includes('Authentication')) {
                  // Session expired - fetchWithAuth will handle redirect via App Bridge
                  setPageNameError('Session expired. Redirecting to re-authenticate...');
                } else if (error.message && error.message.includes('404')) {
                  setPageName('');
                  setPageNameError('Page not created yet. Please try refreshing again.');
                } else {
                  setPageNameError(error.message || 'Failed to load page name. You can still edit it after refreshing the page.');
                }
              } finally {
                setPageNameLoading(false);
              }
            };
            loadPageName();
          }, 1000); // Wait 1 second for page to be created
        }
      } else {
        setRefreshError('Failed to refresh page. Please try again.');
      }
    } catch (error: any) {
      console.error('Error refreshing page:', error);
      let errorMessage = error.message || 'Failed to refresh page. Please try again.';
      
      // Check for authentication errors
      // Note: 401 errors are handled centrally by fetchWithAuth (App Bridge Redirect)
      // We just show a simple error message here, no popup or manual redirect
      if (error.status === 401 || errorMessage.includes('401') || errorMessage.includes('Authentication') || errorMessage.includes('Invalid API key')) {
        setRefreshError('Session expired. Redirecting to re-authenticate...');
        // fetchWithAuth will handle the redirect via App Bridge Redirect
      } else {
        setRefreshError(errorMessage);
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Page title="Admin Settings">
      {/* Success Banner */}
      {refreshSuccess && (
        <div style={{ marginBottom: '20px' }}>
          <Banner status="success" onDismiss={() => setRefreshSuccess(false)}>
            Customer page refreshed successfully! The page has been updated with the latest configuration. Your customizations (title, URL, SEO settings) have been preserved.
          </Banner>
        </div>
      )}

      {/* Error Banner */}
      {refreshError && (
        <div style={{ marginBottom: '20px' }}>
          <Banner status="critical" onDismiss={() => setRefreshError('')}>
            {refreshError}
          </Banner>
        </div>
      )}

      {/* Preview Link Banner */}
      <div style={{ 
        backgroundColor: '#f6f6f7', 
        padding: '16px 20px', 
        marginBottom: '20px',
        borderRadius: '8px',
        border: '1px solid #e1e3e5'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div>
            <strong style={{ fontSize: '14px', color: '#202223' }}>
              Customer Page
            </strong>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6d7175' }}>
              Your order status page is live at: <code>{previewUrl}</code>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              loading={refreshing}
              onClick={handleRefreshPage}
              tone="critical"
            >
              🔄 Refresh Page
            </Button>
            <a 
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#008060',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#006e52'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#008060'}
            >
              View Customer Page →
            </a>
          </div>
        </div>
      </div>

      {/* Page Name Editor */}
      <Card>
        <BlockStack gap="400">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <TextField
                label="Page Name"
                value={pageName}
                onChange={setPageName}
                placeholder="Enter page name..."
                disabled={pageNameLoading || pageNameSaving}
                helpText="Change the name of your customer page in Shopify"
                error={pageNameError}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <Button
                primary
                onClick={handleSavePageName}
                loading={pageNameSaving}
                disabled={pageNameLoading || !pageName.trim()}
              >
                Save Page Name
              </Button>
            </div>
          </div>
          {pageNameSuccess && (
            <Banner status="success" onDismiss={() => setPageNameSuccess(false)}>
              Page name updated successfully!
            </Banner>
          )}
          {pageNameLoading && (
            <p style={{ fontSize: '13px', color: '#6d7175', margin: 0 }}>
              Loading page name...
            </p>
          )}
        </BlockStack>
      </Card>

      <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
        {selectedTab === 0 && (
          <div style={{ marginTop: '20px' }}>
            <FAQManagement shop={shop} />
          </div>
        )}
        {selectedTab === 1 && (
          <div style={{ marginTop: '20px' }}>
            <AdminSettings shop={shop} />
          </div>
        )}
        {selectedTab === 2 && (
          <div style={{ marginTop: '20px' }}>
            <AISettings shop={shop} />
          </div>
        )}
        {selectedTab === 3 && (
          <div style={{ marginTop: '20px' }}>
            <ContactFormSettings shop={shop} />
          </div>
        )}
      </Tabs>
    </Page>
  );
}

export default App;

